import React, { useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useRequestReferralLinkMutation } from '../hooks/useRequestReferralLinkMutation';
import { CopyIcon } from '../../assets/icons';
import { useNotification } from '../../notification/notificationsContext';
import { Customer } from '../types';
import { useLocalStorage } from '@rehooks/local-storage';

const ReferralLinkDetails = (props: { link: string }) => {
  const analytics = useAnalytics();
  const source = 'billing';

  const { link } = props;
  const { notifySuccess, notifyError } = useNotification();
  const [expanded, onExpandedChanged] = useLocalStorage<boolean>(
    'BillingPortalReferralLinkDetails/expanded',
    true
  );

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([link], { type: 'text/plain' }),
        }),
      ]);
      analyticsTrack(analytics, AnalyticsEvent.REFERRAL_LINK_COPIED, {
        source,
      });
      notifySuccess({ notificationMsg: 'Copied to clipboard' });
    } catch (e) {
      notifyError({ notificationMsg: `Failed to copy to clipboard` });
    }
  }, [link, analytics, notifySuccess, notifyError]);

  return expanded ? (
    <div className="referral-link-details__container">
      <div className="billing-flex-table__row--base">
        <div className="col-2 billing-table__column align-self-start growegy-title14--moderat">
          Referral link
        </div>
        <div className="col-10 billing-table__column--small-padding d-flex" onClick={onCopy}>
          <span className="growegy-label14--moderrat">{link}</span>
          <div style={{ marginLeft: '20px' }}>
            <CopyIcon />
          </div>
        </div>
      </div>
      <div className="d-flex flex-column growegy-title12 billing-flex-table__row-max-width">
        <div>
          <span>
            Simply copy and share your unique referral link with businesses who could benefit from
            Growegy. Everyone who signs up and makes their first payment through your link earns you
            a 10% reward on the following billing cycle. For all the conditions, please review our{' '}
            <a href="https://www.growegy.com/terms/" target="_blank" rel="noopener noreferrer">
              Terms & Conditions
            </a>
          </span>
        </div>
      </div>
      <div className="d-flex">
        <div className="ml-auto">
          <Button
            variant="light"
            className="upgrade-to-annual__hide-button"
            onClick={() => {
              onExpandedChanged(false);
              analyticsTrack(analytics, AnalyticsEvent.REFERRAL_COLLAPSED, {
                source: 'banner',
              });
            }}
          >
            Hide
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div>
      <Button
        variant="primary"
        className="request-referral-link__apply-button ml-auto"
        onClick={() => {
          onExpandedChanged(true);
          analyticsTrack(analytics, AnalyticsEvent.REFERRAL_EXPANDED, {
            source: 'banner',
          });
        }}
      >
        {`See Personal Referral Link`}
      </Button>
    </div>
  );
};

export const RequestReferralLink = (props: { customer: Customer | undefined }) => {
  const analytics = useAnalytics();
  const source = 'billing';
  const { mutate: upgradePlanMutation } = useRequestReferralLinkMutation({ source });
  const referralLink = props.customer?.referralLink;
  if (!referralLink) {
    return null;
  }

  if (!!referralLink.link) {
    return <ReferralLinkDetails link={referralLink.link} />;
  }

  if (referralLink.wasRequested) {
    return (
      <div className="request-referral-link__container--link-requested">
        Your referral link will appear here soon. Check back later!
      </div>
    );
  }

  return (
    <div className="request-referral-link__container--link-is-not-requested">
      <div>
        Get your personal referral link and earn 10% on each new customer's first payment when you
        share Growegy.
      </div>
      <div className="upgrade-to-annual__footer">
        <Button
          variant="primary"
          className="request-referral-link__apply-button"
          onClick={() => {
            upgradePlanMutation();
            analyticsTrack(analytics, AnalyticsEvent.REFERRAL_LINK_CREATE_REQUEST_SENT, {
              source,
            });
          }}
        >{`Get referral link`}</Button>
      </div>
      <div className="d-flex growegy-title12">
        <span>
          View our{' '}
          <a href="https://www.growegy.com/terms/" target="_blank" rel="noopener noreferrer">
            Terms & Conditions
          </a>
        </span>
      </div>
    </div>
  );
};
